import React, { useState } from 'react'
import { useResume } from '../ResumeProvider'
import { Button } from '~/lib/ui/button'
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from '~/lib/ui/select'
import { MagicWand } from 'iconoir-react'
import { LightBulbIcon } from '@heroicons/react/24/outline'
import Cursor from './Cursor'
import { useEditorItem } from './EditorItemProvider'
import useAiStream from './useAiStream'
import { useTranslation } from '~/hooks/useTranslation'
import AylaTimeoutError from '~/components/shared/AylaTimeout'

const AiSummary = () => {
  const { t } = useTranslation('AiSummary')
  const { item, updateItem } = useEditorItem()

  const {
    persistResumeIfChanged,
    documentId: resumeDocumentId,
    resumeDocument: { work, education, skills },
  } = useResume()

  const hasWork = work && work.length > 0
  const hasEducation = education && education.length > 0
  const hasSkills =
    skills &&
    skills.length > 0 &&
    Object.keys(skills[0]).filter((k) => k !== 'key').length > 0

  const notEnoughData = !hasWork && !hasEducation && !hasSkills

  const [panelOpen, setPanelOpen] = useState(false)
  const [numberOfSentences, setNumberOfSentences] = useState('4')
  const [desiredJobTitle, setDesiredJobTitle] = useState('')
  const error = t('error')

  const beforeStartStream = async () => await persistResumeIfChanged()
  const {
    displayedText,
    typingComplete,
    streamNotStarted,
    streamRunning,
    streamComplete,
    streamFailed,
    resetResponse,
    startStream,
  } = useAiStream({ beforeStartStream, notEnoughData, error })

  const generateAiSummary = async () => {
    const eventUrl = `/resume/builder/${resumeDocumentId}/ai_summary_sse?desired_job_title=${desiredJobTitle}&number_of_sentences=${numberOfSentences}`
    await startStream(eventUrl)
  }

  const selectResponse = () => {
    const newItem = { ...item, summary: displayedText }
    updateItem(newItem)
    closePanel()
  }

  const closePanel = () => {
    resetResponse()
    setDesiredJobTitle('')
    setPanelOpen(false)
  }

  if (!resumeDocumentId) {
    return (
      <div className="rounded-lg bg-lily-blue p-2 md:col-span-2">
        <LightBulbIcon className="mr-2 inline h-6 w-6" />
        <span className="mr-2 inline font-bold">Tip:</span>
        <span className="inline">{t('save_to_generate_ai_summary')}</span>
      </div>
    )
  }

  if (!panelOpen) {
    return (
      <div className="flex justify-between md:col-span-2">
        <Button onClick={() => setPanelOpen(true)}>
          <MagicWand /> <span className="pl-2">{t('generate_ai_summary')}</span>
        </Button>
      </div>
    )
  }

  return (
    <div className="rounded-lg bg-lily-blue p-4 md:col-span-2">
      <div className="flex pb-4 font-bold">
        <MagicWand /> {t('generate_ai_summary')}
      </div>
      {streamNotStarted && (
        <>
          <div className="pb-4">{t('ai_summary_description_detail')}</div>
          <div className="pb-4">
            <label htmlFor="desired_job_title" className="block font-bold">
              {t('desired_job_title')}
            </label>
            <input
              type="text"
              id="desired_job_title"
              name="desired_job_title"
              value={desiredJobTitle}
              onChange={(e) => setDesiredJobTitle(e.target.value)}
              className="w-full border border-solid border-input bg-input-background p-2"
            />
          </div>
          <div className="pb-4">
            {/* CTS-9213 — second label was misassociated to desired_job_title; now correctly targets the sentences select */}
            <label htmlFor="number-of-sentences" className="block font-bold">
              {t('how_many_sentences')}
            </label>
            <Select
              value={numberOfSentences}
              onValueChange={setNumberOfSentences}
            >
              <SelectTrigger id="number-of-sentences">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {['1', '2', '3', '4', '5'].map((val) => (
                  <SelectItem key={val} value={val}>
                    {val}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </>
      )}
      {streamRunning && (
        <div className="pb-4">
          <div>{t('please_wait')}</div>
        </div>
      )}
      {streamFailed && <AylaTimeoutError onRetry={generateAiSummary} />}
      {!streamFailed && displayedText.length > 0 && (
        <div className="mb-4 block w-full border border-solid border-input bg-input-background p-4">
          {displayedText} <Cursor show={!typingComplete} />
        </div>
      )}
      <div className="flex flex-row justify-between">
        {!streamComplete && !streamFailed && (
          <>
            <Button
              onClick={generateAiSummary}
              disabled={streamRunning || !typingComplete}
            >
              {t('lets_go_ayla')}
            </Button>
            {streamNotStarted && (
              <Button onClick={closePanel} variant="outline">
                {t('cancel')}
              </Button>
            )}
          </>
        )}
        {streamFailed && (
          <Button onClick={closePanel} variant="outline">
            {t('cancel')}
          </Button>
        )}
        {streamComplete && typingComplete && (
          <>
            <Button onClick={selectResponse}>{t('use_this_summary')}</Button>
            <Button onClick={closePanel} variant="outline">
              {t('discard')}
            </Button>
          </>
        )}
      </div>
    </div>
  )
}
export default AiSummary
